import React from "react";
import * as THREE from "three";

const {
  useEffect: useH8SolutionsEffect,
  useRef: useH8SolutionsRef,
  useState: useH8SolutionsState,
} = React;

function ThreeStage({ color = "#a8a4ff", style, variant = "ai" }) {
  const common = {
    fill: "none",
    stroke: color,
    strokeWidth: 1.25,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  let drawing;
  if (variant === "product") {
    drawing = (
      <>
        {[0, 1, 2, 3, 4].map((index) => (
          <g key={index} opacity={index === 2 ? 1 : 0.52}>
            <rect x={37 + index * 30} y={index === 2 ? 48 : 66 - index * 3} width="16" height={index === 2 ? 78 : 52 + index * 5} rx="2" {...common} />
            <path d={`M${37 + index * 30} ${index === 2 ? 48 : 66 - index * 3}l8-6 16 0-8 6`} {...common} opacity=".65" />
          </g>
        ))}
        <path d="M24 128h166" {...common} opacity=".28" />
      </>
    );
  } else if (variant === "dev") {
    drawing = (
      <>
        <g transform="translate(67 38) rotate(-5 61 45)">
          <rect width="122" height="90" rx="4" {...common} />
          <path d="M0 17h122M13 34h72M13 48h96M13 62h58" {...common} opacity=".56" />
          <circle cx="11" cy="9" r="2" fill={color} stroke="none" />
          <circle cx="19" cy="9" r="2" fill={color} stroke="none" opacity=".7" />
        </g>
        <g transform="translate(25 64) rotate(5 22 44)">
          <rect width="44" height="88" rx="7" {...common} />
          <path d="M8 20h27M8 34h21M8 48h27M8 62h17" {...common} opacity=".56" />
        </g>
        <path d="M63 91 81 76M68 120l73-12" {...common} opacity=".28" strokeDasharray="4 5" />
      </>
    );
  } else if (variant === "website") {
    drawing = (
      <>
        <rect x="33" y="35" width="132" height="95" rx="5" {...common} />
        <path d="M33 54h132M48 72h71M48 87h96M48 102h58M48 117h82" {...common} opacity=".5" />
        <circle cx="45" cy="45" r="2" fill={color} stroke="none" />
        <circle cx="53" cy="45" r="2" fill={color} stroke="none" opacity=".7" />
        <path d="m169 115 22 20M178 112l13 23" {...common} opacity=".4" />
        <path d="m176 116 8-8 8 8-8 8z" {...common} />
      </>
    );
  } else {
    const nodes = [[31, 89], [54, 55], [78, 104], [101, 70], [123, 43], [145, 93], [174, 65], [112, 121], [65, 137]];
    drawing = (
      <>
        <g {...common} opacity=".34">
          <path d="M31 89 54 55 101 70 123 43 174 65 145 93 112 121 65 137 78 104 31 89M54 55l24 49 67-11M101 70l11 51M78 104l67-11" />
        </g>
        {nodes.map(([cx, cy], index) => (
          <circle key={index} cx={cx} cy={cy} r={index === 3 ? 7 : 3.2} fill={index === 3 ? "none" : color} {...common} opacity={index === 3 ? 0.9 : 0.72} />
        ))}
      </>
    );
  }

  return (
    <div className="ni-static-three-stage" style={{ position: "absolute", inset: 0, ...style }} aria-hidden="true">
      <svg viewBox="0 0 220 170" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block", filter: `drop-shadow(0 0 16px ${color}33)` }}>
        {drawing}
      </svg>
    </div>
  );
}

function SolutionCard({ eyebrow, title, body, tags, cta, href, visual, accent, visualSlotRef, webglReady = false, visualLabel }) {
  const accentColor = {
    indigo: "oklch(72% 0.13 270)",
    vermilion: "oklch(72% 0.14 35)",
    moss: "oklch(72% 0.10 158)",
  }[accent];
  return (
    <a
      href={href}
      style={{
        background: "var(--ink-2)",
        borderRadius: "var(--r-4)",
        border: "1px solid var(--line-on-ink)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 560,
        textDecoration: "none",
        color: "inherit",
        transition: "transform var(--dur-base) var(--ease-rain), background var(--dur-base), border-color var(--dur-base)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--ink-3)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--ink-2)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{
        height: 240,
        background: "linear-gradient(180deg, oklch(22% 0.02 245) 0%, oklch(14% 0.015 245) 100%)",
        position: "relative", overflow: "hidden",
        borderBottom: "1px solid var(--line-on-ink)",
      }}>
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(ellipse at 60% 40%, black 0%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at 60% 40%, black 0%, transparent 70%)",
        }} />
        {visualSlotRef ? <div ref={visualSlotRef} aria-hidden="true" style={{ position: "absolute", inset: 0, padding: 24, color: accentColor }}>
          {!webglReady && visual}
          <div className="mono" style={{ position: "absolute", left: 24, bottom: 18, fontSize: 10, color: "var(--on-ink-subtle)", letterSpacing: "0.12em" }}>{visualLabel}</div>
        </div> : <div style={{ position: "absolute", inset: 0, padding: 24, color: accentColor }}>{visual}</div>}
      </div>
      <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
        <div className="mono" style={{ fontSize: 11, color: "var(--on-ink-subtle)", letterSpacing: "0.1em" }}>{eyebrow}</div>
        <h3 style={{ color: "var(--on-ink)" }}>{title}</h3>
        <p style={{ color: "var(--on-ink-muted)", fontSize: 15, lineHeight: 1.6 }}>{body}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
          {tags.map((t) => (<span key={t} className="tag tag-ink">{t}</span>))}
        </div>
        <div style={{ flex: 1 }} />
        <div className="flex items-center between" style={{
          paddingTop: 16, borderTop: "1px solid var(--line-on-ink-soft)", color: "var(--on-ink)",
        }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>{cta}</span>
          <span style={{ fontSize: 18 }}>→</span>
        </div>
      </div>
    </a>
  );
}

const H8_SOLUTIONS_PATHS = [
  {
    no: "01",
    kind: "transformation",
    label: "AI AND DELIVERY",
    title: "AI Transformation to Digital Delivery",
    body: "Choose a workflow, test the smallest useful idea, and build the product, system, or integration that follows.",
    href: "#ai-transformation",
    summary: "Workflow rails move from a starting question through a test ring into a stable delivery structure.",
  },
  {
    no: "02",
    kind: "marketing",
    label: "MARKETING AND CX",
    title: "AI Marketing & Customer Experience",
    body: "Turn customer attention into clearer conversations, useful next steps, and human follow-up when needed.",
    href: "#ai-marketing",
    summary: "A customer signal follows a conversation path before reaching a clearly marked human handover relay.",
  },
  {
    no: "03",
    kind: "workforce",
    label: "WORKFORCE",
    title: "Workforce & Team Solutions",
    body: "Bring together people, delivery support, and digital tools around the way the work needs to run.",
    href: "#workforce",
    summary: "Role markers coordinate around a stable operating plane and shared delivery core.",
  },
];

function H8SolutionFallback({ kind }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.35,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (kind === "marketing") return (
    <svg viewBox="0 0 240 170" preserveAspectRatio="xMidYMid meet">
      <path d="M26 112C58 112 61 49 103 61s31 64 72 34c16-12 19-27 34-28" {...common} opacity=".48" />
      <circle cx="82" cy="67" r="18" {...common} opacity=".72" />
      <circle cx="118" cy="78" r="24" {...common} opacity=".4" />
      <path d="M190 112V58m-12 54h24M181 66l9-9 9 9" {...common} />
      <circle cx="190" cy="55" r="4" fill="currentColor" stroke="none" />
      <circle cx="43" cy="105" r="4" fill="currentColor" stroke="none" opacity=".72" />
    </svg>
  );
  if (kind === "workforce") return (
    <svg viewBox="0 0 240 170" preserveAspectRatio="xMidYMid meet">
      <ellipse cx="120" cy="105" rx="72" ry="30" {...common} opacity=".42" />
      <ellipse cx="120" cy="105" rx="38" ry="16" {...common} opacity=".74" />
      <path d="M120 89v32M84 78l24 18m48-18-24 18M70 111l34-4m66 4-34-4" {...common} opacity=".48" />
      {[[84,76],[156,76],[68,112],[172,112],[103,131],[137,131]].map(([x,y], index) => (
        <g key={index}><circle cx={x} cy={y-7} r="4" fill="currentColor" stroke="none" opacity=".82" /><path d={`M${x} ${y-2}v12`} {...common} /></g>
      ))}
      <path d="M104 101h32v15h-32z" {...common} />
    </svg>
  );
  return (
    <svg viewBox="0 0 240 170" preserveAspectRatio="xMidYMid meet">
      <path d="M30 51h39l34 34h40l30-29h35M30 85h54l19 0m40 0h65M30 119h39l34-34" {...common} opacity=".46" />
      <circle cx="30" cy="51" r="4" fill="currentColor" stroke="none" />
      <circle cx="30" cy="85" r="4" fill="currentColor" stroke="none" opacity=".72" />
      <circle cx="30" cy="119" r="4" fill="currentColor" stroke="none" opacity=".54" />
      <circle cx="123" cy="85" r="25" {...common} opacity=".78" />
      <circle cx="123" cy="85" r="15" {...common} opacity=".42" />
      <path d="M174 47v76m0-76h35m-35 38h35m-35 38h35" {...common} />
    </svg>
  );
}

function h8SolutionsRail(from, to, radius, material, radialSegments = 10) {
  const direction = new THREE.Vector3().subVectors(to, from);
  const geometry = new THREE.CylinderGeometry(radius, radius, direction.length(), radialSegments, 1, false);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(from).add(to).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());
  return mesh;
}

function h8SolutionsCurve(points, radius, material, tubularSegments = 64) {
  const curve = new THREE.CatmullRomCurve3(points, false, "centripetal", 0.4);
  const geometry = new THREE.TubeGeometry(curve, tubularSegments, radius, 10, false);
  return { curve, mesh: new THREE.Mesh(geometry, material) };
}

function h8SolutionsMaterials() {
  return {
    ink: new THREE.MeshPhysicalMaterial({ color: 0x101b2b, roughness: 0.34, metalness: 0.28, clearcoat: 0.32 }),
    cobalt: new THREE.MeshPhysicalMaterial({ color: 0x3154a2, roughness: 0.2, metalness: 0.15, clearcoat: 0.72, clearcoatRoughness: 0.16, transparent: true, opacity: 0.86 }),
    cobaltGlass: new THREE.MeshPhysicalMaterial({ color: 0x3f62ad, roughness: 0.1, metalness: 0.04, transmission: 0.28, thickness: 0.48, ior: 1.32, clearcoat: 1, transparent: true, opacity: 0.64, depthWrite: false }),
    pearl: new THREE.MeshPhysicalMaterial({ color: 0xf1eee5, roughness: 0.2, metalness: 0.04, clearcoat: 0.58, transparent: true, opacity: 0.94 }),
    cyan: new THREE.MeshPhysicalMaterial({ color: 0x71c7d5, roughness: 0.2, clearcoat: 0.7 }),
    lime: new THREE.MeshPhysicalMaterial({ color: 0xb5c968, roughness: 0.24, clearcoat: 0.62 }),
    coral: new THREE.MeshPhysicalMaterial({ color: 0xd87968, roughness: 0.22, clearcoat: 0.68 }),
  };
}

function h8SolutionsSceneBase() {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 50);
  camera.position.set(0, 0.35, 6.1);
  camera.lookAt(0, 0, 0);
  scene.add(new THREE.HemisphereLight(0xeef2ff, 0x08111e, 1.35));
  const key = new THREE.DirectionalLight(0xfffbef, 2.25);
  key.position.set(-3.4, 4.8, 5.8);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x6e8bd5, 1.05);
  rim.position.set(4.2, 1.2, 3.4);
  scene.add(rim);
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(1.72, 48),
    new THREE.MeshBasicMaterial({ color: 0x050a12, transparent: true, opacity: 0.24, depthWrite: false }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.scale.set(1.35, 0.42, 1);
  floor.position.set(0, -1.32, -0.7);
  scene.add(floor);
  return { scene, camera };
}

function h8BuildTransformationScene() {
  const { scene, camera } = h8SolutionsSceneBase();
  const materials = h8SolutionsMaterials();
  const root = new THREE.Group();
  root.rotation.set(-0.08, 0.12, -0.03);
  scene.add(root);

  const starts = [
    new THREE.Vector3(-2.05, 0.72, -0.18),
    new THREE.Vector3(-2.18, 0.02, 0.12),
    new THREE.Vector3(-2.02, -0.7, -0.12),
  ];
  const gate = new THREE.Vector3(-0.28, 0, 0.18);
  const delivery = [
    new THREE.Vector3(1.15, 0.76, 0.05),
    new THREE.Vector3(1.15, 0, 0.05),
    new THREE.Vector3(1.15, -0.76, 0.05),
  ];
  const sourceGeometry = new THREE.SphereGeometry(0.115, 18, 12);
  starts.forEach((point, index) => {
    const node = new THREE.Mesh(sourceGeometry, index === 0 ? materials.cyan : materials.pearl);
    node.position.copy(point);
    node.scale.setScalar(index === 0 ? 1.08 : 0.82);
    root.add(node);
    const path = h8SolutionsCurve([
      point,
      new THREE.Vector3(-1.35, point.y * 0.72, 0.18 + index * 0.06),
      new THREE.Vector3(-0.72, point.y * 0.28, 0.28),
      gate,
    ], 0.028, index === 1 ? materials.pearl : materials.cobalt, 54);
    root.add(path.mesh);
  });

  const testRings = [0.3, 0.48, 0.66].map((radius, index) => {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.032, 10, 54), index === 1 ? materials.cobaltGlass : materials.pearl);
    ring.position.copy(gate);
    ring.rotation.set(Math.PI / 2 + index * 0.18, 0.28 - index * 0.12, index * 0.38);
    root.add(ring);
    return ring;
  });
  const testCore = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.25, 0.32, 24), materials.cobalt);
  testCore.rotation.x = Math.PI / 2;
  testCore.position.copy(gate);
  root.add(testCore);

  delivery.forEach((point, index) => {
    const route = h8SolutionsCurve([
      gate,
      new THREE.Vector3(0.42, point.y * 0.22, 0.3),
      new THREE.Vector3(0.78, point.y * 0.68, 0.18),
      point,
    ], 0.032, index === 1 ? materials.pearl : materials.cobalt, 46);
    root.add(route.mesh);
  });
  const frameLeft = 1.16;
  const frameRight = 1.96;
  [-0.8, 0, 0.8].forEach((y, index) => {
    root.add(h8SolutionsRail(new THREE.Vector3(frameLeft, y, 0.05), new THREE.Vector3(frameRight, y, 0.05), 0.045, index === 1 ? materials.pearl : materials.cobalt));
  });
  root.add(h8SolutionsRail(new THREE.Vector3(frameLeft, -0.82, 0.05), new THREE.Vector3(frameLeft, 0.82, 0.05), 0.052, materials.cobalt));
  root.add(h8SolutionsRail(new THREE.Vector3(frameRight, -0.82, 0.05), new THREE.Vector3(frameRight, 0.82, 0.05), 0.052, materials.pearl));
  const buildMarker = new THREE.Mesh(new THREE.SphereGeometry(0.095, 16, 12), materials.lime);
  root.add(buildMarker);
  const fullCurve = new THREE.CatmullRomCurve3([
    starts[1], new THREE.Vector3(-1.2, 0.04, 0.28), gate,
    new THREE.Vector3(0.58, 0, 0.3), delivery[1], new THREE.Vector3(frameRight, 0, 0.05),
  ], false, "centripetal", 0.4);

  return {
    scene,
    camera,
    ownedMaterials: Object.values(materials),
    update: (time) => {
      root.rotation.y = 0.12 + Math.sin(time * 0.14) * 0.055;
      root.rotation.x = -0.08 + Math.sin(time * 0.11) * 0.022;
      testRings.forEach((ring, index) => { ring.rotation.z = index * 0.38 + Math.sin(time * 0.22 + index) * 0.16; });
      buildMarker.position.copy(fullCurve.getPointAt((time * 0.045) % 1));
    },
  };
}

function h8BuildMarketingScene() {
  const { scene, camera } = h8SolutionsSceneBase();
  const materials = h8SolutionsMaterials();
  const root = new THREE.Group();
  root.rotation.set(-0.04, -0.12, 0.02);
  scene.add(root);
  const signalPath = h8SolutionsCurve([
    new THREE.Vector3(-2.1, -0.58, 0.08),
    new THREE.Vector3(-1.42, 0.56, 0.2),
    new THREE.Vector3(-0.56, 0.64, 0.14),
    new THREE.Vector3(0.16, -0.16, 0.28),
    new THREE.Vector3(0.92, -0.18, 0.16),
    new THREE.Vector3(1.42, 0.44, 0.08),
  ], 0.045, materials.cobaltGlass, 86);
  root.add(signalPath.mesh);
  const conversationRings = [0.3, 0.52].map((radius, index) => {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, index === 0 ? 0.034 : 0.025, 10, 52), index === 0 ? materials.pearl : materials.cobalt);
    ring.position.copy(signalPath.curve.getPointAt(index === 0 ? 0.36 : 0.57));
    ring.rotation.set(Math.PI / 2 - 0.2, 0.28, index * 0.54);
    root.add(ring);
    return ring;
  });
  const conversationCore = new THREE.Mesh(new THREE.SphereGeometry(0.16, 18, 14), materials.cyan);
  conversationCore.position.copy(signalPath.curve.getPointAt(0.49));
  root.add(conversationCore);

  const relay = new THREE.Group();
  relay.position.set(1.55, 0.12, 0.04);
  relay.add(new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.36, 0.72, 24), materials.ink));
  const relayRing = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.032, 10, 48), materials.pearl);
  relayRing.rotation.x = Math.PI / 2;
  relayRing.position.y = 0.22;
  relay.add(relayRing);
  const handoverSignal = new THREE.Mesh(new THREE.SphereGeometry(0.11, 18, 12), materials.coral);
  handoverSignal.position.y = 0.52;
  relay.add(handoverSignal);
  root.add(relay);
  root.add(h8SolutionsRail(new THREE.Vector3(1.06, -0.54, 0), new THREE.Vector3(1.06, 0.64, 0), 0.025, materials.pearl, 8));
  root.add(h8SolutionsRail(new THREE.Vector3(1.06, 0.64, 0), new THREE.Vector3(1.52, 0.64, 0), 0.025, materials.pearl, 8));
  const movingSignal = new THREE.Mesh(new THREE.SphereGeometry(0.105, 18, 12), materials.cyan);
  root.add(movingSignal);

  return {
    scene,
    camera,
    ownedMaterials: Object.values(materials),
    update: (time) => {
      root.rotation.y = -0.12 + Math.sin(time * 0.13) * 0.06;
      movingSignal.position.copy(signalPath.curve.getPointAt((time * 0.052) % 1));
      conversationRings[0].rotation.z = Math.sin(time * 0.2) * 0.18;
      conversationRings[1].rotation.z = 0.54 - Math.sin(time * 0.17) * 0.13;
      relay.position.y = 0.12 + Math.sin(time * 0.25) * 0.025;
    },
  };
}

function h8BuildWorkforceScene() {
  const { scene, camera } = h8SolutionsSceneBase();
  const materials = h8SolutionsMaterials();
  camera.position.set(0, 1.15, 6.25);
  camera.lookAt(0, -0.18, 0);
  const root = new THREE.Group();
  root.rotation.y = 0.1;
  scene.add(root);
  const plane = new THREE.Mesh(new THREE.CircleGeometry(1.62, 64), materials.ink);
  plane.rotation.x = -Math.PI / 2;
  plane.position.y = -0.52;
  root.add(plane);
  [0.72, 1.18, 1.62].forEach((radius, index) => {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.018 + index * 0.004, 8, 72), index === 1 ? materials.cobalt : materials.pearl);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = -0.48 + index * 0.012;
    root.add(ring);
  });
  const core = new THREE.Group();
  core.position.y = -0.2;
  core.add(new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.56, 0.46, 28), materials.cobaltGlass));
  const coreTop = new THREE.Mesh(new THREE.TorusGeometry(0.36, 0.045, 10, 48), materials.pearl);
  coreTop.rotation.x = Math.PI / 2;
  coreTop.position.y = 0.24;
  core.add(coreTop);
  root.add(core);

  const roleNodes = [];
  const roleGeometry = new THREE.CylinderGeometry(0.075, 0.095, 0.32, 14);
  const headGeometry = new THREE.SphereGeometry(0.1, 16, 12);
  for (let index = 0; index < 6; index += 1) {
    const angle = (index / 6) * Math.PI * 2 + 0.2;
    const x = Math.cos(angle) * 1.42;
    const z = Math.sin(angle) * 1.08;
    const role = new THREE.Group();
    role.position.set(x, -0.16, z);
    const stem = new THREE.Mesh(roleGeometry, index === 0 ? materials.cyan : materials.cobalt);
    const head = new THREE.Mesh(headGeometry, index === 4 ? materials.coral : materials.pearl);
    head.position.y = 0.25;
    role.add(stem, head);
    role.userData.baseY = role.position.y;
    root.add(role);
    roleNodes.push(role);
    root.add(h8SolutionsRail(new THREE.Vector3(x * 0.34, -0.47, z * 0.34), new THREE.Vector3(x * 0.83, -0.47, z * 0.83), 0.018, materials.cobaltGlass, 7));
  }
  const coordinationMarker = new THREE.Mesh(new THREE.SphereGeometry(0.09, 16, 12), materials.lime);
  root.add(coordinationMarker);

  return {
    scene,
    camera,
    ownedMaterials: Object.values(materials),
    update: (time) => {
      root.rotation.y = 0.1 + Math.sin(time * 0.1) * 0.045;
      roleNodes.forEach((role, index) => { role.position.y = role.userData.baseY + Math.sin(time * 0.24 + index * 0.7) * 0.022; });
      const phase = time * 0.16;
      coordinationMarker.position.set(Math.cos(phase) * 1.18, -0.42, Math.sin(phase) * 0.9);
      core.rotation.y = Math.sin(time * 0.16) * 0.12;
    },
  };
}

function VariantWorkforceFallback() {
  const line = { fill: "none", stroke: "currentColor", strokeWidth: 1.45, strokeLinecap: "round", strokeLinejoin: "round" };
  return <div className="ni-static-three-stage" style={{ position: "absolute", inset: 0 }}><svg viewBox="0 0 220 170" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block", filter: "drop-shadow(0 0 16px #9fb4ff33)" }}>
    <path d="M34 118h152M48 112v-15m124 15v-15M69 107l39-25m43 25-39-25" {...line} opacity=".34" /><path d="M110 78h24v20h-24zM114 84h16m-8-6v20" {...line} opacity=".88" />
    {[[37,84],[161,84],[57,116],[142,116]].map(([x, y], index) => <g key={index} opacity={index === 0 ? 1 : .7}><path d={`M${x} ${y}h18v22H${x}z`} {...line} /><path d={`M${x + 4} ${y + 8}h10M${x + 4} ${y + 13}h7`} {...line} opacity=".5" /></g>)}
    <path d="M48 147h124" {...line} opacity=".22" />
  </svg></div>;
}

const H8_VARIANT_SOLUTION_CARDS = [
  { eyebrow: "01 / AI AND DELIVERY", title: "AI Transformation to Digital Delivery", body: "Choose a workflow, test the smallest useful idea, and build the product, system, or integration that follows.", tags: ["Workflow design", "PoC", "MVP", "Integrations"], cta: "Explore this path", href: "#ai-transformation", visual: <ThreeStage color="#a8a4ff" variant="ai" />, accent: "indigo", visualLabel: "QUESTION ▸ TEST ▸ DELIVERY" },
  { eyebrow: "02 / MARKETING AND CX", title: "AI Marketing & Customer Experience", body: "Turn customer attention into clearer conversations, useful next steps, and human follow-up when needed.", tags: ["Attract", "Understand", "Respond", "Human handover"], cta: "Explore this path", href: "#ai-marketing", visual: <ThreeStage color="#7ad6a8" variant="dev" />, accent: "moss", visualLabel: "ATTENTION ▸ CONVERSATION ▸ HANDOVER" },
  { eyebrow: "03 / WORKFORCE", title: "Workforce & Team Solutions", body: "Bring together people, delivery support, and digital tools around the way the work needs to run.", tags: ["People", "Roles", "Delivery support", "Operating model"], cta: "Explore this path", href: "#workforce", visual: <VariantWorkforceFallback />, accent: "indigo", visualLabel: "ROLES ▸ TEAM ▸ DELIVERY" },
];

function variantAI3DScene() {
  const scene = new THREE.Scene(); const camera = new THREE.PerspectiveCamera(34, 1, .1, 50); camera.position.set(0, 0, 5.2);
  const points = [[-1.7,-.2],[ -1.3,.75],[-.78,-.7],[-.35,.25],[.22,.88],[.68,-.18],[1.18,.55],[1.6,-.42],[.42,-.84],[-.98,.08]];
  const point3 = ([x, y]) => new THREE.Vector3(x, y, Math.sin(x * 2 + y) * .25);
  const spatialPoints = points.map(point3);
  const geometry = new THREE.BufferGeometry(); geometry.setAttribute("position", new THREE.Float32BufferAttribute(spatialPoints.flatMap(({ x, y, z }) => [x, y, z]), 3));
  const nodes = new THREE.Points(geometry, new THREE.PointsMaterial({ color: 0xb6b0ff, size: .12, transparent: true, opacity: .92, sizeAttenuation: true }));
  const links = [[0,1],[0,2],[1,3],[2,3],[1,4],[3,4],[3,5],[4,6],[5,6],[5,8],[6,7],[7,8],[8,2],[2,9],[9,0]]; const vertices = [];
  links.forEach(([from, to]) => vertices.push(points[from][0], points[from][1], Math.sin(points[from][0] * 2 + points[from][1]) * .25, points[to][0], points[to][1], Math.sin(points[to][0] * 2 + points[to][1]) * .25));
  const lineGeometry = new THREE.BufferGeometry(); lineGeometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3)); const lineMaterial = new THREE.LineBasicMaterial({ color: 0x8d86ff, transparent: true, opacity: .34 }); const line = new THREE.LineSegments(lineGeometry, lineMaterial);
  const coreMaterial = new THREE.MeshBasicMaterial({ color: 0xb6b0ff, wireframe: true, transparent: true, opacity: .55 }); const core = new THREE.Mesh(new THREE.IcosahedronGeometry(.34, 1), coreMaterial); core.position.set(.05,.12,.2); const group = new THREE.Group(); group.add(nodes, line, core); scene.add(group);
  const signalGeometry = new THREE.SphereGeometry(.105, 16, 12); const signalMaterial = new THREE.MeshBasicMaterial({ color: 0xf3f1ff, transparent: true, opacity: .98 });
  const signalPaths = [[0,1,3,5,8,7],[9,0,2,3,4,6],[2,8,5,6,7]].map((path) => path.map((index) => spatialPoints[index]));
  const signals = signalPaths.map(() => { const signal = new THREE.Mesh(signalGeometry, signalMaterial); group.add(signal); return signal; });
  return { scene, camera, update: (time) => {
    group.rotation.y = Math.sin(time * .17) * .24; group.rotation.x = Math.sin(time * .12) * .08;
    const pulse = .5 + .5 * Math.sin(time * 1.08); core.rotation.y = time * .36; core.scale.setScalar(.86 + pulse * .3); coreMaterial.opacity = .42 + pulse * .28; lineMaterial.opacity = .28 + pulse * .18;
    signals.forEach((signal, index) => { const path = signalPaths[index]; const point = (time * .125 + index * .34) % 1 * (path.length - 1); const from = Math.floor(point); signal.position.lerpVectors(path[from], path[Math.min(from + 1, path.length - 1)], point - from); signal.scale.setScalar(.76 + .32 * (.5 + .5 * Math.sin(time * 1.18 + index))); });
  } };
}

function variantMarketing3DScene() {
  const scene = new THREE.Scene(); const camera = new THREE.PerspectiveCamera(34, 1, .1, 50); camera.position.set(0, 0, 5.4); const group = new THREE.Group();
  const panel = (width, height) => { const item = new THREE.Group(); item.add(new THREE.Mesh(new THREE.PlaneGeometry(width, height), new THREE.MeshBasicMaterial({ color: 0x16241d, transparent: true, opacity: .92, side: THREE.DoubleSide }))); item.add(new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(width, height)), new THREE.LineBasicMaterial({ color: 0x7ad6a8, transparent: true, opacity: .9 }))); [0,1,2].forEach((row) => { const bar = new THREE.Mesh(new THREE.PlaneGeometry((row % 2 ? .45 : .7) * width, .06), new THREE.MeshBasicMaterial({ color: 0x7ad6a8, transparent: true, opacity: .55 })); bar.position.set(-width * .12, height / 2 - (row + 1) * height / 4, .01); item.add(bar); }); return item; };
  const browser = panel(2.6, 1.8); browser.position.set(.7,.35,0); browser.rotation.set(-.18,-.32,0); const phone = panel(.95,1.95); phone.position.set(-1.25,-.25,.8); phone.rotation.set(-.1,.32,.08); const cubeGeometry = new THREE.BoxGeometry(.4,.4,.4); const cubeMaterial = new THREE.MeshBasicMaterial({ color: 0x16241d, transparent: true, opacity: .95 }); const cube = new THREE.Mesh(cubeGeometry, cubeMaterial); cube.add(new THREE.LineSegments(new THREE.EdgesGeometry(cubeGeometry), new THREE.LineBasicMaterial({ color: 0xa6e8c2, transparent: true, opacity: .95 }))); cube.position.set(1.55,-.85,-.4);
  const linkMaterial = new THREE.LineBasicMaterial({ color: 0x7ad6a8, transparent: true, opacity: .32 }); const link = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints([phone.position.clone(),browser.position.clone(),browser.position.clone(),cube.position.clone(),phone.position.clone(),cube.position.clone()]), linkMaterial);
  const signal = new THREE.Mesh(new THREE.SphereGeometry(.14, 16, 12), new THREE.MeshBasicMaterial({ color: 0xe9ffef, transparent: true, opacity: .98 })); const route = [phone.position.clone(), browser.position.clone(), cube.position.clone(), phone.position.clone()]; group.add(browser,phone,cube,link,signal); scene.add(group);
  return { scene, camera, update: (time) => {
    group.rotation.y = Math.sin(time * .17) * .25; const beat = .5 + .5 * Math.sin(time * 1.04);
    browser.position.y = .35 + Math.sin(time * .46) * .11; browser.rotation.z = Math.sin(time * .28) * .028;
    phone.position.y = -.25 + Math.sin(time * .46 + 1.4) * .12; phone.rotation.z = .08 + Math.sin(time * .33) * .035;
    cube.rotation.x = time * .45; cube.rotation.y = time * .59; cube.position.y = -.85 + Math.sin(time * .62 + .6) * .14; cube.scale.setScalar(.92 + beat * .22); cubeMaterial.opacity = .75 + beat * .2; linkMaterial.opacity = .24 + beat * .26;
    const flow = (time * .11) % 1 * 3; const from = Math.floor(flow); signal.position.lerpVectors(route[from], route[Math.min(from + 1, route.length - 1)], flow - from); signal.scale.setScalar(.76 + beat * .42);
  } };
}

function variantWorkforce3DScene() {
  const scene = new THREE.Scene(); const camera = new THREE.PerspectiveCamera(34, 1, .1, 50); camera.position.set(0,.7,5.8); camera.lookAt(0,-.18,0); const group = new THREE.Group();
  const plane = new THREE.Mesh(new THREE.CircleGeometry(1.8,48), new THREE.MeshBasicMaterial({ color: 0x0c1628, transparent: true, opacity: .72 })); plane.rotation.x = -Math.PI / 2; plane.scale.set(1.24,.58,1); plane.position.y = -.56; group.add(plane);
  const coreGeometry = new THREE.BoxGeometry(.68,.22,.68); const coreMaterial = new THREE.MeshBasicMaterial({ color: 0x162441, transparent: true, opacity: .7 }); const core = new THREE.Mesh(coreGeometry, coreMaterial); core.position.y = -.27; core.add(new THREE.LineSegments(new THREE.EdgesGeometry(coreGeometry), new THREE.LineBasicMaterial({ color: 0x9fb4ff, transparent: true, opacity: .9 }))); group.add(core);
  const connectorMaterials = []; const roles = [[-1.35,.62],[1.35,.62],[-1.08,-.78],[1.08,-.78]].map(([x,z], index) => { const geometry = new THREE.BoxGeometry(.3,.35,.3); const marker = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: index === 0 ? 0x314f9d : 0x19243a, transparent: true, opacity: .84 })); marker.position.set(x,-.3,z); marker.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry), new THREE.LineBasicMaterial({ color: index === 0 ? 0xa7b8ff : 0x7892cf, transparent: true, opacity: .78 }))); group.add(marker); const connectorMaterial = new THREE.LineBasicMaterial({ color: 0x7892cf, transparent: true, opacity: .34 }); connectorMaterials.push(connectorMaterial); group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(x,-.51,z),new THREE.Vector3(x*.46,-.5,z*.46),new THREE.Vector3(0,-.5,0)]), connectorMaterial)); marker.userData = { baseY: marker.position.y, baseX: x, baseZ: z }; return marker; });
  const ringMaterial = new THREE.MeshBasicMaterial({ color: 0x7892cf, transparent: true, opacity: .32 }); const ring = new THREE.Mesh(new THREE.TorusGeometry(1.13,.018,8,48), ringMaterial); ring.rotation.x = Math.PI / 2; ring.position.y = -.54; ring.scale.set(1.18,.62,1); const coordinator = new THREE.Mesh(new THREE.SphereGeometry(.11, 16, 12), new THREE.MeshBasicMaterial({ color: 0xebf0ff, transparent: true, opacity: .96 })); coordinator.position.set(0,-.24,.1); group.add(ring, coordinator); scene.add(group);
  return { scene, camera, update: (time) => {
    group.rotation.y = .08 + Math.sin(time * .12) * .1; const beat = .5 + .5 * Math.sin(time * .86);
    core.rotation.y = time * .22; core.scale.setScalar(.92 + beat * .2); coreMaterial.opacity = .58 + beat * .24;
    roles.forEach((marker,index) => { const move = .07 + .09 * (.5 + .5 * Math.sin(time * .46 + index * 1.57)); marker.position.x = marker.userData.baseX * (1 - move); marker.position.z = marker.userData.baseZ * (1 - move); marker.position.y = marker.userData.baseY + Math.sin(time * .48 + index * .75) * .05; marker.rotation.y = time * .16 + index * .6; });
    ring.rotation.z = time * .095; ring.scale.set(1.18 + beat * .09, .62 + beat * .045, 1 + beat * .075); ringMaterial.opacity = .2 + beat * .32; connectorMaterials.forEach((material,index) => { material.opacity = .22 + .14 * (.5 + .5 * Math.sin(time * .86 + index)); }); coordinator.position.set(Math.cos(time * .31) * .78, -.22 + Math.sin(time * .62) * .07, Math.sin(time * .31) * .54); coordinator.scale.setScalar(.8 + beat * .42);
  } };
}

function H8SolutionsExperience({ sectionRef, variant = false }) {
  const canvasRef = useH8SolutionsRef(null);
  const gridRef = useH8SolutionsRef(null);
  const slotsRef = useH8SolutionsRef([]);
  const [webglReady, setWebglReady] = useH8SolutionsState(false);

  useH8SolutionsEffect(() => {
    const section = sectionRef.current;
    const grid = gridRef.current;
    const canvas = canvasRef.current;
    const slots = slotsRef.current.filter(Boolean);
    if (!section || !grid || !canvas || slots.length !== 3) return undefined;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobileQuery = window.matchMedia("(max-width: 720px)");
    let renderer;
    let scenes = [];
    let observer;
    let resizeObserver;
    let rafId = null;
    let lastFrame = null;
    let elapsedMs = motionQuery.matches ? 4600 : 0;
    let intersecting = false;
    let disposed = false;
    let contextFailed = false;
    let readyAnnounced = false;
    const debug = {
      frameCount: 0,
      renderCount: 0,
      resizeCount: 0,
      elapsedMs: 0,
      intersecting: false,
      animating: false,
      reducedMotion: motionQuery.matches,
      webglReady: false,
      slotRects: [],
      rendererCount: 0,
      getState() {
        return {
          frameCount: this.frameCount,
          renderCount: this.renderCount,
          resizeCount: this.resizeCount,
          elapsedMs: Math.round(this.elapsedMs),
          intersecting: this.intersecting,
          animating: this.animating,
          reducedMotion: this.reducedMotion,
          webglReady: this.webglReady,
          slotRects: this.slotRects,
          rendererCount: this.rendererCount,
          visibilityState: document.visibilityState,
        };
      },
    };
    const canAnimate = () => !disposed
      && !contextFailed
      && intersecting
      && document.visibilityState === "visible"
      && !motionQuery.matches;
    const stop = () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = null;
      lastFrame = null;
      debug.animating = false;
      canvas.dataset.animating = "false";
    };
    const render = (timeSeconds) => {
      if (!renderer || contextFailed) return false;
      const canvasBounds = grid.getBoundingClientRect();
      const slotRects = slots.map((slot) => slot.getBoundingClientRect());
      debug.slotRects = slotRects.map((rect) => ({ width: Math.round(rect.width), height: Math.round(rect.height) }));
      renderer.setScissorTest(false);
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, true, true);
      renderer.setScissorTest(true);
      scenes.forEach((entry, index) => {
        const rect = slotRects[index];
        const x = rect.left - canvasBounds.left;
        const y = canvasBounds.height - (rect.bottom - canvasBounds.top);
        if (rect.width <= 0 || rect.height <= 0) return;
        entry.camera.aspect = rect.width / rect.height;
        entry.camera.updateProjectionMatrix();
        entry.update(timeSeconds);
        renderer.setViewport(x, y, rect.width, rect.height);
        renderer.setScissor(x, y, rect.width, rect.height);
        renderer.clear(true, true, true);
        renderer.render(entry.scene, entry.camera);
      });
      renderer.setScissorTest(false);
      debug.renderCount += 1;
      debug.elapsedMs = elapsedMs;
      canvas.dataset.slotCount = String(slotRects.length);
      canvas.dataset.renderCount = String(debug.renderCount);
      canvas.dataset.elapsedMs = String(Math.round(elapsedMs));
      if (!readyAnnounced) {
        readyAnnounced = true;
        debug.webglReady = true;
        canvas.dataset.webglReady = "true";
        setWebglReady(true);
      }
      return true;
    };
    const resizeAndRender = () => {
      if (!renderer || contextFailed) return;
      const bounds = grid.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, variant ? 1.5 : (mobileQuery.matches ? 1.15 : 1.6));
      renderer.setPixelRatio(dpr);
      renderer.setSize(Math.round(bounds.width), Math.round(bounds.height), false);
      canvas.dataset.dpr = String(dpr);
      debug.resizeCount += 1;
      render((motionQuery.matches ? 4600 : elapsedMs) / 1000);
    };
    const frame = (now) => {
      if (!canAnimate()) {
        stop();
        return;
      }
      if (lastFrame !== null) elapsedMs += Math.min(40, now - lastFrame);
      lastFrame = now;
      debug.frameCount += 1;
      debug.animating = true;
      canvas.dataset.frameCount = String(debug.frameCount);
      canvas.dataset.animating = "true";
      render(elapsedMs / 1000);
      rafId = window.requestAnimationFrame(frame);
    };
    const sync = () => {
      debug.intersecting = intersecting;
      debug.reducedMotion = motionQuery.matches;
      canvas.dataset.intersecting = intersecting ? "true" : "false";
      canvas.dataset.reducedMotion = motionQuery.matches ? "true" : "false";
      canvas.dataset.visibility = document.visibilityState;
      if (motionQuery.matches) {
        stop();
        render(4.6);
        return;
      }
      if (canAnimate() && rafId === null) rafId = window.requestAnimationFrame(frame);
      if (!canAnimate()) stop();
    };
    const onVisibilityChange = () => sync();
    const onMotionChange = () => sync();
    const onResize = () => resizeAndRender();
    const onContextLost = (event) => {
      event.preventDefault();
      contextFailed = true;
      debug.webglReady = false;
      canvas.dataset.webglReady = "false";
      canvas.style.display = "none";
      setWebglReady(false);
      stop();
    };
    const onContextRestored = () => {
      contextFailed = true;
      debug.webglReady = false;
      setWebglReady(false);
    };

    try {
      if (!canvas.getContext("webgl2") && !canvas.getContext("webgl")) throw new Error("WebGL unavailable");
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: !mobileQuery.matches, powerPreference: "high-performance" });
      renderer.setClearColor(0x000000, 0);
      renderer.autoClear = false;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.08;
      if ("outputColorSpace" in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
      debug.rendererCount = 1;
      canvas.dataset.rendererCount = "1";
      scenes = variant
        ? [variantAI3DScene(), variantMarketing3DScene(), variantWorkforce3DScene()]
        : [h8BuildTransformationScene(), h8BuildMarketingScene(), h8BuildWorkforceScene()];
      canvas.dataset.sceneCount = String(scenes.length);
      resizeObserver = new ResizeObserver(resizeAndRender);
      resizeObserver.observe(grid);
      slots.forEach((slot) => resizeObserver.observe(slot));
      observer = new IntersectionObserver(([entry]) => {
        intersecting = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        sync();
      }, { threshold: [0, 0.25, 1] });
      observer.observe(section);
      document.addEventListener("visibilitychange", onVisibilityChange);
      motionQuery.addEventListener("change", onMotionChange);
      mobileQuery.addEventListener("change", onResize);
      window.addEventListener("resize", onResize);
      canvas.addEventListener("webglcontextlost", onContextLost, false);
      canvas.addEventListener("webglcontextrestored", onContextRestored, false);
      resizeAndRender();
      sync();
    } catch (error) {
      contextFailed = true;
      debug.webglReady = false;
      canvas.dataset.webglReady = "false";
      canvas.style.display = "none";
      setWebglReady(false);
    }

    return () => {
      disposed = true;
      stop();
      if (observer) observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      motionQuery.removeEventListener("change", onMotionChange);
      mobileQuery.removeEventListener("change", onResize);
      window.removeEventListener("resize", onResize);
      canvas.removeEventListener("webglcontextlost", onContextLost, false);
      canvas.removeEventListener("webglcontextrestored", onContextRestored, false);
      const geometries = new Set();
      const materials = new Set();
      scenes.forEach(({ scene, ownedMaterials = [] }) => {
        ownedMaterials.forEach((material) => materials.add(material));
        scene.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
        objectMaterials.filter(Boolean).forEach((material) => materials.add(material));
        });
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
      if (renderer) renderer.dispose();
    };
  }, [sectionRef, variant]);

  if (variant) return (
    <div className="grid-solutions h8-variant-solutions-grid" ref={gridRef} data-webgl-ready={webglReady ? "true" : "false"} style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, position: "relative" }}>
      <canvas className="h8-variant-solutions-canvas" ref={canvasRef} aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 2, display: "block", width: "100%", height: "100%", pointerEvents: "none" }} />
      {H8_VARIANT_SOLUTION_CARDS.map((path, index) => <SolutionCard key={path.title} {...path} webglReady={webglReady} visualSlotRef={(node) => { slotsRef.current[index] = node; }} />)}
    </div>
  );

  return (
    <div className={`grid-solutions h8-solutions-grid${webglReady ? " is-webgl-ready" : ""}`} ref={gridRef} data-webgl-ready={webglReady ? "true" : "false"}>
      <canvas className="h8-solutions-canvas" ref={canvasRef} aria-hidden="true" />
      {H8_SOLUTIONS_PATHS.map((path, index) => (
        <a className="h8-solution-card" href={path.href} key={path.kind}>
          <div className="h8-solution-visual" ref={(node) => { slotsRef.current[index] = node; }} aria-hidden="true">
            <div className="h8-solution-fallback" hidden={webglReady}><H8SolutionFallback kind={path.kind} /></div>
          </div>
          <div className="h8-solution-copy">
            <div className="mono h8-solution-label">PATH / {path.no} · {path.label}</div>
            <h3>{path.title}</h3>
            <p>{path.body}</p>
            <div className="h8-solution-cta"><span>Explore this path</span><span aria-hidden="true">↓</span></div>
          </div>
          <span className="h8-solutions-sr">{path.summary}</span>
        </a>
      ))}
    </div>
  );
}

function Solutions() {
  const variantSectionRef = useH8SolutionsRef(null);
  return (
    <section
      className="section-pad section-dark"
      id="solutions"
      ref={variantSectionRef}
      style={{ position: "relative", overflow: "hidden" }}
    >
      <div aria-hidden="true" style={{
        position: "absolute", top: -300, right: -200, width: 900, height: 900,
        background: "radial-gradient(circle, oklch(45% 0.14 270 / 0.18) 0%, transparent 60%)",
      }} />
      <div className="container" style={{ position: "relative" }}>
        <div className="section-header">
          <div className="eyebrow">Core capabilities</div>
          <h2 style={{ color: "var(--on-ink)" }}>
            Build what your business needs —{" "}
            <span style={{ color: "oklch(80% 0.10 270)" }}>with AI-ready product thinking.</span>
          </h2>
          <p className="lead">
            Strategy, design, development, and launch support in one team.
          </p>
          {/* The "All Services" link pointed at a services index page that no
              longer exists — the full list is the grid directly below. */}
        </div>

        <H8SolutionsExperience sectionRef={variantSectionRef} variant />

        {/* Featured service is intentionally absent from the ai-marketing-motion review variant. */}
      </div>
    </section>
  );
}

export { Solutions };
